import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useI18n} from '@/i18n'
import {Strong} from '@/components/shared/Strong'
import {MutedText} from '@/components/shared/MutedText'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {AuthType, ScimOutboundConfigResponseFields, DatabaseOutboundConfigCreateRequestFields, RestOutboundConfigCreateRequestFields} from '@/types/outbound-connector'

export interface ConnectorAuthFormState {
    authType: AuthType
    tokenSecretRef: string
    tokenUrl: string
    clientId: string
    clientSecretRef: string
    scope: string
    username: string
    passwordSecretRef: string
}

export interface ConnectorHttpFormState {
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    retryBackoffMs: number
}

export type ConnectorConfigFormState = ConnectorAuthFormState & ConnectorHttpFormState

type AuthFormUpdate = Partial<ConnectorConfigFormState>

interface ConnectorConfigFormProps {
    form: ConnectorConfigFormState
    onChange: (updates: AuthFormUpdate) => void
    mode: 'create' | 'edit'
    showRetryFields?: boolean
    authTypeOptions?: AuthType[]
}

const DEFAULT_AUTH_OPTIONS: AuthType[] = [AuthType.BEARER_TOKEN, AuthType.OAUTH2_CLIENT, AuthType.BASIC_AUTH]

export function ConnectorConfigForm({
    form,
    onChange,
    mode,
    showRetryFields = true,
    authTypeOptions = DEFAULT_AUTH_OPTIONS,
}: ConnectorConfigFormProps) {
    const i18n = useI18n()

    const getAuthTypeLabel = (type: AuthType) => {
        switch (type) {
            case AuthType.BEARER_TOKEN: return i18n.connectors.bearerToken
            case AuthType.OAUTH2_CLIENT: return i18n.connectors.oauth2Client
            case AuthType.BASIC_AUTH: return i18n.connectors.basicAuth
            case AuthType.TOKEN: return i18n.connectors.tokenAuth
            case AuthType.CERTIFICATE: return i18n.connectors.certificateAuth
            case AuthType.KUBECONFIG: return i18n.connectors.kubeconfigAuth
            case AuthType.NONE: return i18n.connectors.noneAuth
        }
    }

    return (
        <>
            {/* Authentication Section */}
            <Stack>
                <Strong>{i18n.connectors.authentication}</Strong>
                <FormGrid>
                    <FormGrid>
                        <Label htmlFor="authType">{i18n.connectors.authType}</Label>
                        <Select
                            value={form.authType}
                            onValueChange={(value) => onChange({authType: value as AuthType})}
                        >
                            <SelectTrigger data-testid="select-auth-type">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                {authTypeOptions.map((opt) => (
                                    <SelectItem key={opt} value={opt}>{getAuthTypeLabel(opt)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FormGrid>

                    {form.authType === AuthType.BEARER_TOKEN && (
                        <FormGrid>
                            <Label htmlFor="tokenSecretRef">{i18n.connectors.tokenSecretRef}</Label>
                            <SecretReferenceInput
                                id="tokenSecretRef"
                                value={form.tokenSecretRef}
                                onChange={(v) => onChange({tokenSecretRef: v})}
                                placeholder={mode === 'create' ? i18n.connectors.tokenSecretRefPlaceholder : i18n.connectors.leaveEmptyToKeep}
                                data-testid="token-secret-ref"
                            />
                            <MutedText size="sm">
                                {mode === 'create' ? i18n.connectors.tokenSecretRefHint : i18n.connectors.leaveEmptyToKeepHint}
                            </MutedText>
                        </FormGrid>
                    )}

                    {form.authType === AuthType.OAUTH2_CLIENT && (
                        <>
                            <FormGrid>
                                <Label htmlFor={ScimOutboundConfigResponseFields.tokenUrl}>{i18n.connectors.tokenUrl}</Label>
                                <Input
                                    id={ScimOutboundConfigResponseFields.tokenUrl}
                                    value={form.tokenUrl}
                                    onChange={(e) => onChange({tokenUrl: e.target.value})}
                                    placeholder={i18n.connectors.tokenUrlPlaceholder}
                                />
                            </FormGrid>
                            <DetailGrid columns={2} responsive={false}>
                                <FormGrid>
                                    <Label htmlFor={ScimOutboundConfigResponseFields.clientId}>{i18n.connectors.clientId}</Label>
                                    <Input
                                        id={ScimOutboundConfigResponseFields.clientId}
                                        value={form.clientId}
                                        onChange={(e) => onChange({clientId: e.target.value})}
                                    />
                                </FormGrid>
                                <FormGrid>
                                    <Label htmlFor="clientSecretRef">{i18n.connectors.clientSecretRef}</Label>
                                    <SecretReferenceInput
                                        id="clientSecretRef"
                                        value={form.clientSecretRef}
                                        onChange={(v) => onChange({clientSecretRef: v})}
                                        placeholder={mode === 'create' ? i18n.connectors.clientSecretRefPlaceholder : i18n.connectors.leaveEmptyToKeep}
                                        data-testid="client-secret-ref"
                                    />
                                </FormGrid>
                            </DetailGrid>
                            <FormGrid>
                                <Label htmlFor={ScimOutboundConfigResponseFields.scope}>{i18n.connectors.scope}</Label>
                                <Input
                                    id={ScimOutboundConfigResponseFields.scope}
                                    value={form.scope}
                                    onChange={(e) => onChange({scope: e.target.value})}
                                    placeholder={i18n.connectors.scopePlaceholder}
                                />
                            </FormGrid>
                        </>
                    )}

                    {form.authType === AuthType.BASIC_AUTH && (
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor={DatabaseOutboundConfigCreateRequestFields.username}>{i18n.connectors.username}</Label>
                                <Input
                                    id={DatabaseOutboundConfigCreateRequestFields.username}
                                    value={form.username}
                                    onChange={(e) => onChange({username: e.target.value})}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="passwordSecretRef">{i18n.connectors.passwordSecretRef}</Label>
                                <SecretReferenceInput
                                    id="passwordSecretRef"
                                    value={form.passwordSecretRef}
                                    onChange={(v) => onChange({passwordSecretRef: v})}
                                    placeholder={mode === 'create' ? i18n.connectors.passwordSecretRefPlaceholder : i18n.connectors.leaveEmptyToKeep}
                                    data-testid="password-secret-ref"
                                />
                            </FormGrid>
                        </DetailGrid>
                    )}
                </FormGrid>
            </Stack>

            {/* HTTP Settings Section */}
            <Stack>
                <Strong>{i18n.connectors.httpSettings}</Strong>
                <DetailGrid columns={2} responsive={false}>
                    <FormGrid>
                        <Label htmlFor={DatabaseOutboundConfigCreateRequestFields.connectTimeoutMs}>{i18n.connectors.connectTimeoutMs}</Label>
                        <Input
                            id={DatabaseOutboundConfigCreateRequestFields.connectTimeoutMs}
                            type="number"
                            value={form.connectTimeoutMs}
                            onChange={(e) => onChange({connectTimeoutMs: parseInt(e.target.value)})}
                        />
                    </FormGrid>
                    <FormGrid>
                        <Label htmlFor={RestOutboundConfigCreateRequestFields.readTimeoutMs}>{i18n.connectors.readTimeoutMs}</Label>
                        <Input
                            id={RestOutboundConfigCreateRequestFields.readTimeoutMs}
                            type="number"
                            value={form.readTimeoutMs}
                            onChange={(e) => onChange({readTimeoutMs: parseInt(e.target.value)})}
                        />
                    </FormGrid>
                    {showRetryFields && (
                        <>
                            <FormGrid>
                                <Label htmlFor={ScimOutboundConfigResponseFields.maxRetries}>{i18n.connectors.maxRetries}</Label>
                                <Input
                                    id={ScimOutboundConfigResponseFields.maxRetries}
                                    type="number"
                                    value={form.maxRetries}
                                    onChange={(e) => onChange({maxRetries: parseInt(e.target.value)})}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor={ScimOutboundConfigResponseFields.retryBackoffMs}>{i18n.connectors.retryBackoffMs}</Label>
                                <Input
                                    id={ScimOutboundConfigResponseFields.retryBackoffMs}
                                    type="number"
                                    value={form.retryBackoffMs}
                                    onChange={(e) => onChange({retryBackoffMs: parseInt(e.target.value)})}
                                />
                            </FormGrid>
                        </>
                    )}
                </DetailGrid>
            </Stack>
        </>
    )
}
